import { memo, type CSSProperties } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Sparkles, MessageCircle, type LucideIcon } from 'lucide-react';
import { cn, formatRespondentFrequency } from '@/lib/utils';
import { deriveConviction, convictionTick } from '@/lib/conviction';
import { NODE_HUE, NODE_LABEL } from '@/lib/node-visuals';
import { findingTypeLabel } from '@/lib/market-categories';
import type { NodeMetadata } from '@/services/canvas-mappers';

export interface NodeData {
  label: string;
  content?: string;
  metadata?: NodeMetadata;
}

interface BaseNodeProps {
  data: NodeData;
  icon: LucideIcon;
  nodeType: string;
  selected?: boolean;
}

function respondentLabel(meta: NodeMetadata | undefined): string {
  return meta?.respondentName || meta?.respondentEmail || 'a respondent';
}

function BaseNodeComponent({ data, icon: Icon, nodeType, selected }: BaseNodeProps) {
  const hue = NODE_HUE[nodeType] ?? '#64748b';
  const meta = data.metadata;
  // Market findings read their category into the type slot ("Competitor
  // finding") so the canvas gains specificity without gaining text.
  const label = nodeType === 'market_signal' ? findingTypeLabel(meta) : (NODE_LABEL[nodeType] ?? nodeType);

  // Conviction — how grounded the node is. The register drives the card's
  // weight/ink; the tick shows the magnitude (a respondent ratio or a source
  // count). Both come from the shared pure helper — see lib/conviction.ts.
  const conviction = deriveConviction({ type: nodeType, metadata: meta });
  const tick = convictionTick({ type: nodeType, metadata: meta });
  const isProvisional = conviction === 'provisional';
  const isEvidenced = conviction === 'evidenced';
  const isFromDialog = meta?.source === 'dialog';
  const isSynthesised = meta?.source === 'synthesis';

  // `--c` sets the node hue for every child (custom properties inherit), so the
  // colour-mix expressions below and in the handles resolve from one source.
  const style = {
    '--c': hue,
    borderStyle: isProvisional ? 'dashed' : 'solid',
    borderColor: `color-mix(in srgb, var(--c) ${isEvidenced ? 42 : 30}%, hsl(var(--border)))`,
    background: isEvidenced ? 'color-mix(in srgb, var(--c) 7%, hsl(var(--card)))' : 'hsl(var(--card))',
    opacity: isProvisional ? 0.9 : 1,
    boxShadow: selected
      ? '0 0 0 2px hsl(var(--card)), 0 0 0 3.5px color-mix(in srgb, var(--c) 60%, transparent)'
      : 'var(--shadow-sm)',
  } as CSSProperties;

  return (
    <div className="w-60 rounded-lg border transition-shadow duration-150" style={style}>
      <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1">
        <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--c)' }} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
          {label}
        </span>
        <span className="flex-1" />

        {/* Conviction tick — grounding magnitude, top-right. */}
        {tick.kind === 'ratio' && (
          <span
            className="flex items-center gap-1.5 shrink-0"
            title={formatRespondentFrequency(tick.count, tick.total) ?? undefined}
          >
            <span className="h-1 w-9 overflow-hidden rounded-full" style={{ background: 'hsl(var(--border))' }}>
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.min(100, Math.round((tick.count / tick.total) * 100))}%`,
                  background: 'color-mix(in srgb, var(--c) 75%, transparent)',
                }}
              />
            </span>
            <span className="font-mono text-[10px] tabular-nums text-ink-soft">
              {tick.count}/{tick.total}
            </span>
          </span>
        )}
        {tick.kind === 'sources' && (
          <span className="flex shrink-0 items-center gap-1 text-[10px] text-ink-soft">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--c)' }} />
            <span className="font-mono tabular-nums">{tick.count}</span>
            <span className="text-muted-foreground">source{tick.count === 1 ? '' : 's'}</span>
          </span>
        )}
        {isProvisional && (
          <span
            className="shrink-0 text-[9px] font-semibold uppercase tracking-wide"
            style={{ color: 'color-mix(in srgb, var(--c) 50%, hsl(var(--muted-foreground)))' }}
          >
            Assumed
          </span>
        )}
      </div>

      {/* Title wraps to up to 3 lines. Cap is fixed regardless of selection so
          node dimensions — and therefore edge attachment points — stay stable.
          Full title + description are always reachable via click → modal. */}
      <div className="px-3 pb-2.5">
        <span
          className={cn(
            'block text-[13px] font-medium leading-snug line-clamp-3',
            isProvisional ? 'italic text-muted-foreground' : 'text-foreground',
          )}
        >
          {data.label}
        </span>

        {isFromDialog && (
          <span className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground">
            <MessageCircle className="h-3 w-3 shrink-0" style={{ color: 'var(--c)' }} />
            <span className="truncate">from dialog · {respondentLabel(meta)}</span>
          </span>
        )}
        {isSynthesised && (
          <span
            className="mt-1.5 flex items-center gap-1 text-[10px] font-medium"
            style={{ color: 'color-mix(in srgb, var(--c) 65%, hsl(var(--ink-soft)))' }}
          >
            <Sparkles className="h-3 w-3 shrink-0" />
            Synthesised
          </span>
        )}
      </div>

      {/* Three edge flows share these handles (edges pick by id):
          - spine, left→right: leaves a source's right, enters a target's left.
          - evidence, top↓: a market signal's bottom drops into its target's top.
          - hypothesis, bottom↑: an assumption's top rises into its target's
            bottom (assumptions band below the spine — see graph-layout.ts).
          The rising flow uses two extra invisible handles rather than flipping
          the visible ones' types: React Flow resolves an edge's handle id
          within the type-matching bucket, so repurposing `bottom` as a target
          would break the spine-to-spine evidences edges (raw item → theme)
          that leave via `bottom` today. */}
      <Handle id="left" type="target" position={Position.Left} className="h-3! w-3!" style={{ background: 'var(--c)', border: '2px solid hsl(var(--card))' }} />
      <Handle id="right" type="source" position={Position.Right} className="h-3! w-3!" style={{ background: 'var(--c)', border: '2px solid hsl(var(--card))' }} />
      <Handle id="top" type="target" position={Position.Top} className="h-3! w-3!" style={{ background: 'var(--c)', border: '2px solid hsl(var(--card))' }} />
      <Handle id="bottom" type="source" position={Position.Bottom} className="h-3! w-3!" style={{ background: 'var(--c)', border: '2px solid hsl(var(--card))' }} />
      <Handle id="top-source" type="source" position={Position.Top} isConnectable={false} className="h-0! w-0! min-h-0! min-w-0! border-0! opacity-0 pointer-events-none" />
      <Handle id="bottom-target" type="target" position={Position.Bottom} isConnectable={false} className="h-0! w-0! min-h-0! min-w-0! border-0! opacity-0 pointer-events-none" />
    </div>
  );
}

export const BaseNode = memo(BaseNodeComponent);
